// Sprint INTADMIN INTADMIN.B1 (#2110) — display-only "should the Internal
// Admin entry point be rendered?" signal.
//
// ⚠️ NON-AUTHORITATIVE. This hook decides whether to DRAW a link. It is not
// an authorization decision and must never be treated as one. The real gates
// are, in order:
//
//   1. InternalAdminGuard, which probes GET /api/internal/session before
//      rendering any part of the internal shell.
//   2. The server: authenticate + requireFullSession + requirePlatformAdmin()
//      on the /api/internal router (apps/api/src/routes/internal.router.ts).
//
// Forging the JWT claim this hook reads gets an attacker a link they can
// already type by hand, and nothing behind it.
//
// ── Why this is not claim-only ──────────────────────────────────────────
//
// #2110 Q7 adjudicated "read the JWT's is_platform_admin claim for
// display-only cosmetic show/hide". That claim is a correct fast path but
// is NOT sufficient on its own: `is_platform_admin: true` is set by exactly
// one code path, platformAuthService.ts:190-194 (POST /auth/platform/login,
// the dedicated email + password + MFA surface). The standard /auth/login
// NEVER sets it, even for a user who does have a platform_admins row —
// that is deliberate (apps/api/src/services/platformAuthService.ts:6).
//
// Since `platform_admins.requires_platform_login` defaults to FALSE and
// requirePlatformAdmin still admits standard-session admins (the legacy
// back-compat path at requirePlatformAdmin.ts:136-144), a claim-only check
// would hide the entry point from most real admins today — i.e. it would
// fail PRD FR-05, the requirement the entry point exists to satisfy.
//
// So: one probe of GET /api/internal/session, whose result is cached in
// sessionStorage for the rest of the browser session. Cost to any user is
// one request per session, not one per page load.
//
// The claim's remaining job is purely to suppress a flash: an admin on a
// platform-login session sees the entry point immediately on first paint
// instead of watching it appear once the probe lands. It is never the final
// answer — see the `optimistic` comment below for why a claim that cannot
// self-correct must not be trusted on its own.

import { useEffect, useState } from 'react';
import { apiFetch } from '../lib/api';
import { decodeJwtPayload } from '../lib/decodeJwtPayload';

interface DisplayClaims {
  userId?: string;
  is_platform_admin?: boolean;
}

// Keyed by userId so switching accounts in the same tab cannot inherit the
// previous user's answer (sessionStorage outlives a logout within a tab).
function cacheKey(userId: string): string {
  return `blueprint_internal_admin_visible:${userId}`;
}

function readCache(userId: string): boolean | null {
  try {
    const raw = sessionStorage.getItem(cacheKey(userId));
    if (raw === null) return null;
    return raw === 'true';
  } catch {
    // Private-mode / disabled storage — degrade to "probe every mount"
    // rather than throwing. Correctness is unaffected; only the caching is.
    return null;
  }
}

function writeCache(userId: string, value: boolean): void {
  try {
    sessionStorage.setItem(cacheKey(userId), String(value));
  } catch {
    /* no-op — see readCache */
  }
}

/**
 * Returns true when the "Internal Admin" entry point should be rendered.
 *
 * Consumers MUST use this to conditionally render — never to toggle CSS
 * visibility. #2110 FR-04 requires the entry point to be genuinely absent
 * from the component tree for non-admins, not merely hidden.
 */
export function useInternalAdminVisibility(): boolean {
  const token = localStorage.getItem('blueprint_token');
  const claims = token ? decodeJwtPayload<DisplayClaims>(token) : null;
  const userId = claims?.userId ?? null;
  const claimSaysAdmin = claims?.is_platform_admin === true;

  // The two synchronous answers — the JWT claim and this session's cached
  // probe result — are DERIVED during render rather than mirrored into
  // state. Storing them would mean writing state from inside the effect
  // (cascading renders, and `react-hooks/set-state-in-effect` rightly
  // flags it), and would go stale whenever `token` changes without the
  // component remounting.
  //
  // The claim is OPTIMISTIC only: it suppresses a flash for an admin on a
  // platform-login session while the confirming probe is in flight. It is
  // not the final word, because it cannot self-correct — requirePlatformAdmin's
  // own header documents a revocation gap "of up to 7 days" for exactly
  // this claim, so an admin who has been demoted or removed keeps claiming
  // to be one until their token expires. Left unconfirmed, the sidebar
  // would show them a link that dead-ends in the guard's silent bounce for
  // a week. The cached probe result below overrides it once it lands.
  const cached = userId !== null ? readCache(userId) : null;
  const optimistic = claimSaysAdmin && cached === null;

  // State is used ONLY for the asynchronous probe result, and is tagged
  // with the userId it belongs to so a previous user's `true` cannot leak
  // to the next user in the same tab.
  const [probe, setProbe] = useState<{ userId: string; visible: boolean } | null>(null);

  useEffect(() => {
    if (!token || !userId) return;
    // Already answered this session — sessionStorage has it.
    if (readCache(userId) !== null) return;

    let cancelled = false;
    apiFetch('/api/internal/session')
      .then(() => {
        if (cancelled) return;
        writeCache(userId, true);
        setProbe({ userId, visible: true });
      })
      .catch((err: unknown) => {
        if (cancelled) return;
        const e = err as { status?: number; body?: { reason?: string } } | null;

        // PLATFORM_LOGIN_REQUIRED is a 403, but it means "you ARE an admin,
        // you just need to re-authenticate on the platform surface". Hiding
        // the entry point from those admins would strand them with no route
        // in except typing the URL.
        const isAdminNeedingPlatformLogin = e?.body?.reason === 'PLATFORM_LOGIN_REQUIRED';
        if (isAdminNeedingPlatformLogin) {
          writeCache(userId, true);
          setProbe({ userId, visible: true });
          return;
        }

        // Only a definitive 403 — "the server looked you up and you are not
        // a platform admin" — may be cached as a negative for the rest of
        // the session. Anything else (401 on an expired token, a network
        // failure while the API restarts, a 5xx) is NOT an answer about who
        // this user is. Caching those would hide the entry point from a real
        // admin for the tab's whole lifetime: the cache key is the userId,
        // so re-authenticating in the same tab would hit the same key, and
        // the effect's readCache short-circuit would never re-probe.
        if (e?.status === 403) {
          writeCache(userId, false);
        }
        setProbe({ userId, visible: false });
      });

    return () => { cancelled = true; };
  }, [token, userId]);

  const probed = probe !== null && probe.userId === userId ? probe.visible : null;

  // Precedence: a landed answer (cache, then this mount's probe) always
  // beats the optimistic claim.
  if (cached !== null) return cached;
  if (probed !== null) return probed;
  return optimistic;
}
